/**
 * TypeScript types for Live Location Activity Dashboard
 * Matches backend DTOs from BizClick.WMS.Shared.DTOs.LocationActivityDTOs
 */

/**
 * Activity type classification with distinct colors
 * Matches backend ActivityType enum
 */
export enum ActivityType {
  Pick = 1,           // Orange - Plukking fra lokasjon
  PutAway = 2,        // Green - Plassering på lokasjon
  Transfer = 3,       // Blue - Flytting mellom lokasjoner
  CorrectionOut = 4,  // Red - Korreksjon ut av lager
  CorrectionIn = 5,   // Green - Korreksjon inn på lager
  Counting = 6,       // Purple - Varetelling
  Receiving = 7,      // Green - Mottak til lokasjon
  Reservation = 8     // Yellow - Reservasjon av lokasjon
}

/**
 * Activity event broadcasted from SignalR
 * Matches backend LocationActivityEvent
 */
export interface LocationActivityEvent {
  binCode: string;
  rackNumber: number;
  activityType: ActivityType;
  timestamp: string; // ISO 8601 format
  operatorId: string;
  itemNo: string;
  quantity: number;
  companyNo: string;
  warehouseId: string;
}

/**
 * Statistics about location activity
 * Matches backend LocationActivityStats
 */
export interface LocationActivityStats {
  companyNo: string;
  warehouseId: string;
  totalActivitiesLast5Minutes: number;
  activeOperators: number;
  activeLocations: number;
  currentQueueSize: number;
  lastActivityTimestamp: string; // ISO 8601 format
}

/**
 * SignalR connection states
 */
export enum ConnectionStatus {
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
  Disconnected = 'Disconnected'
}

/**
 * Connection quality metrics
 */
export interface ConnectionQuality {
  latency: number; // milliseconds
  packetLoss: number; // percentage 0-100
  lastPingTimestamp?: string;
}

/**
 * Hook return type for useLocationActivity
 */
export interface UseLocationActivityReturn {
  // Connection state
  connectionStatus: ConnectionStatus;
  connectionQuality: ConnectionQuality;
  
  // Event handling
  lastEvent: LocationActivityEvent | null;
  
  // Methods
  joinWarehouseGroup: (warehouseId: string) => Promise<void>;
  joinRackGroup: (warehouseId: string, rackNumber: number) => Promise<void>;
  leaveGroup: (groupName: string) => Promise<void>;
  disconnect: () => void;
}

/**
 * Activity type to color mapping for UI
 * Updated: PutAway now uses blue to distinguish from Receiving (green)
 */
export const ActivityTypeColors = {
  [ActivityType.Pick]: 'bg-orange-500/30',
  [ActivityType.PutAway]: 'bg-blue-500/30',        // Changed from green to blue per WMS best practice
  [ActivityType.Transfer]: 'bg-purple-500/30',     // Changed from blue to purple for distinction
  [ActivityType.CorrectionOut]: 'bg-red-500/30',
  [ActivityType.CorrectionIn]: 'bg-emerald-500/30', // Distinct green for corrections in
  [ActivityType.Counting]: 'bg-slate-500/30',      // Gray/slate for background process
  [ActivityType.Receiving]: 'bg-green-500/30',
  [ActivityType.Reservation]: 'bg-yellow-500/30'
} as const;

/**
 * Detailed color classes for rich activity cards
 * Provides full styling including gradients, borders, and icon colors
 */
export const ActivityTypeDetailedColors = {
  [ActivityType.Pick]: {
    bg: 'bg-gradient-to-br from-orange-50 to-orange-50/30',
    border: 'border-l-4 border-orange-500',
    icon: 'bg-orange-500 text-white',
    badge: 'bg-orange-100 text-orange-700',
    text: 'text-orange-700'
  },
  [ActivityType.PutAway]: {
    bg: 'bg-gradient-to-br from-blue-50 to-blue-50/30',
    border: 'border-l-4 border-blue-500',
    icon: 'bg-blue-500 text-white',
    badge: 'bg-blue-100 text-blue-700',
    text: 'text-blue-700'
  },
  [ActivityType.Transfer]: {
    bg: 'bg-gradient-to-br from-purple-50 to-purple-50/30',
    border: 'border-l-4 border-purple-500',
    icon: 'bg-purple-500 text-white',
    badge: 'bg-purple-100 text-purple-700',
    text: 'text-purple-700'
  },
  [ActivityType.CorrectionOut]: {
    bg: 'bg-gradient-to-br from-red-50 to-red-50/30',
    border: 'border-l-4 border-red-500',
    icon: 'bg-red-500 text-white',
    badge: 'bg-red-100 text-red-700',
    text: 'text-red-700'
  },
  [ActivityType.CorrectionIn]: {
    bg: 'bg-gradient-to-br from-emerald-50 to-emerald-50/30',
    border: 'border-l-4 border-emerald-500',
    icon: 'bg-emerald-500 text-white',
    badge: 'bg-emerald-100 text-emerald-700',
    text: 'text-emerald-700'
  },
  [ActivityType.Counting]: {
    bg: 'bg-gradient-to-br from-slate-50 to-slate-50/30',
    border: 'border-l-4 border-slate-500',
    icon: 'bg-slate-500 text-white',
    badge: 'bg-slate-100 text-slate-700',
    text: 'text-slate-700'
  },
  [ActivityType.Receiving]: {
    bg: 'bg-gradient-to-br from-green-50 to-green-50/30',
    border: 'border-l-4 border-green-500',
    icon: 'bg-green-500 text-white',
    badge: 'bg-green-100 text-green-700',
    text: 'text-green-700'
  },
  [ActivityType.Reservation]: {
    bg: 'bg-gradient-to-br from-yellow-50 to-yellow-50/30',
    border: 'border-l-4 border-yellow-500',
    icon: 'bg-yellow-500 text-white',
    badge: 'bg-yellow-100 text-yellow-700',
    text: 'text-yellow-700'
  }
} as const;

/**
 * Activity type to display name mapping (Norwegian)
 */
export const ActivityTypeNames = {
  [ActivityType.Pick]: 'Plukking',
  [ActivityType.PutAway]: 'Plassering',
  [ActivityType.Transfer]: 'Overføring',
  [ActivityType.CorrectionOut]: 'Korreksjon Ut',
  [ActivityType.CorrectionIn]: 'Korreksjon Inn',
  [ActivityType.Counting]: 'Telling',
  [ActivityType.Receiving]: 'Mottak',
  [ActivityType.Reservation]: 'Reservasjon'
} as const;
